<template>
  <ImportScheduleConfigModal
    :modal-active="modalActive && !confirming"
    :is-importing="isLoading"
    confirm-text="Copiar requisitos"
    @close="emit('close')"
    @select="onSelect"
  />
  <ConfirmDeleteAction
    :show="confirming"
    title="Substituir os requisitos atuais?"
    description="A cópia é independente: mudar a escala de origem depois não altera esta escala. Os requisitos que você já montou aqui serão substituídos."
    text-button="Substituir requisitos"
    @close-modal="cancelReplace"
    @handle-confirm="confirmReplace"
  />
</template>

<script setup lang="ts">
import { ref } from 'vue'
import { useToast } from 'vue-toastification'

import Network from '@/Network'
import ConfirmDeleteAction from '@/components/designSystem/modals/confirm.delete.action.vue'
import ImportScheduleConfigModal from '@/components/ManageScale/Schedule/modals/ImportScheduleConfigModal.vue'
import { useErrorToast } from '@/composables/useErrorToast'
import type { QualificationRuleGroup } from '@/types/qualification-policy.types'

import { clonePolicy, isPolicyEmpty } from './policyModel'

const props = defineProps<{ modalActive: boolean; hasCurrentRequirements: boolean }>()
const emit = defineEmits<{
  close: []
  copy: [rules: QualificationRuleGroup, sourcePolicyId: number, sourceCompanyId: number | null]
}>()

const toast = useToast()
const { notifyError } = useErrorToast()
const isLoading = ref(false)
const confirming = ref(false)
type PendingCopy = {
  rules: QualificationRuleGroup
  sourcePolicyId: number
  sourceCompanyId: number | null
}
const pending = ref<PendingCopy | null>(null)

/**
 * Copia localmente (GET da política vigente da origem, A3.5): o gestor revisa antes de salvar.
 * A cópia é independente (D42) — clone profundo, nunca a árvore da origem.
 */
async function onSelect(scheduleId: number, companyId: number | null) {
  if (isLoading.value) return
  isLoading.value = true
  try {
    const { current } = await Network.qualificationEligibility.getSchedulePolicy(scheduleId)
    if (!current || isPolicyEmpty(current.rules)) {
      toast.info('A escala escolhida não tem qualificação exigida para copiar.')
      return
    }
    const copy: PendingCopy = {
      rules: clonePolicy(current.rules),
      sourcePolicyId: current.policyId,
      sourceCompanyId: companyId
    }
    if (props.hasCurrentRequirements) {
      pending.value = copy
      confirming.value = true
      return
    }
    emit('copy', copy.rules, copy.sourcePolicyId, copy.sourceCompanyId)
  } catch (error) {
    notifyError(error, 'Não foi possível copiar os requisitos da escala escolhida')
  } finally {
    isLoading.value = false
  }
}

function confirmReplace() {
  const copy = pending.value
  if (copy) emit('copy', copy.rules, copy.sourcePolicyId, copy.sourceCompanyId)
  pending.value = null
  confirming.value = false
}

/** Volta para a escolha da escala (o seletor reabre do zero). */
function cancelReplace() {
  pending.value = null
  confirming.value = false
}
</script>
